import { useState } from 'react';
import { publicUrl } from '@src/publicUrl';
import {
  AppHeader,
  Badge,
  Bar,
  Brand,
  Button,
  Card,
  Cluster,
  Columns,
  Container,
  Frame,
  Drawer,
  FoldPanel,
  Footer,
  Hamburger,
  IconBox,
  InlineCode,
  Link,
  Page,
  Panel,
  Row,
  RowList,
  Snippet,
  Specimens,
  Stack,
  ThemeToggle,
  Typography,
  Well,
} from '@src/components';
import { GiftIcon } from '@src/components/icons/GiftIcon';

function DrawerSpecimen() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Cluster gap={4}>
        <Hamburger open={open} onClick={() => setOpen(true)} />
        <Button variant="secondary" size="small" onClick={() => setOpen(true)}>
          Open drawer
        </Button>
      </Cluster>
      <Drawer open={open} onClose={() => setOpen(false)}>
        <Link href="#layout" onClick={() => setOpen(false)}>
          Back to the app
        </Link>
        <ThemeToggle />
      </Drawer>
    </>
  );
}

/**
 * Every layout and chrome block. Pages compose these — they do not write CSS.
 */
export function LayoutSection() {
  return (
    <FoldPanel
      id="layout"
      title="Layout"
      hint="The only place spacing, width and chrome are allowed to live. A page imports these; it does not add a stylesheet."
      example={`<Page>
  <AppHeader actions={<Link href="/">Back</Link>}>
    <Brand />
  </AppHeader>
  <Container width="measure" gutter="narrow" pad="page">
    <Stack gap={8}>…</Stack>
  </Container>
</Page>`}
    >
      <Specimens label="Stack" note="A column. gap is a --space-* step. This is most of every screen.">
        <Panel padding="medium">
          <Stack gap={3}>
            <Typography role="title/card">Create a game</Typography>
            <Typography role="body/sm">Name it, send the link, draw.</Typography>
            <Typography role="meta/helper">One column, three rows, gap 3.</Typography>
          </Stack>
        </Panel>
      </Specimens>

      <Specimens label="Cluster" note="A wrapping row. Labels, chips, a title next to a hint. wrap={false} keeps a trailing action on the same line.">
        <Cluster gap={3} align="baseline">
          <Typography role="meta/caps">Primary</Typography>
          <Typography role="meta/helper">The single most important action.</Typography>
        </Cluster>
      </Specimens>

      <Specimens
        label="Bar"
        note="A Cluster that pushes its ends apart. Title left, actions right. Wraps on a narrow screen."
      >
        <Bar wide>
          <Typography role="title/sub">Players</Typography>
          <Cluster gap={3}>
            <Link href="#layout">Invite</Link>
            <Badge>6</Badge>
          </Cluster>
        </Bar>
      </Specimens>

      <Specimens
        label="Container"
        note="Max-width and gutters. measure / content / wide / narrow. The 48rem gutter shrink lives only here."
      >
        <Well>
          <Stack gap={3}>
            <Container width="narrow">
              <Panel padding="small">
                <Typography role="meta/mono">width="narrow"</Typography>
              </Panel>
            </Container>
            <Container width="measure">
              <Panel padding="small">
                <Typography role="meta/mono">width="measure" — reading width</Typography>
              </Panel>
            </Container>
            <Container width="wide">
              <Panel padding="small">
                <Typography role="meta/mono">width="wide" — home, 90rem</Typography>
              </Panel>
            </Container>
          </Stack>
        </Well>
      </Specimens>

      <Specimens
        label="Columns"
        note="2 — two tracks, one on narrow. wideLast stretches a leftover last card."
      >
        <Columns columns={2} gap={4} wideLast>
          <Panel padding="small">
            <Typography role="body/sm">Left</Typography>
          </Panel>
          <Panel padding="small">
            <Typography role="body/sm">Right</Typography>
          </Panel>
          <Panel padding="small">
            <Typography role="body/sm">Full-width leftover</Typography>
          </Panel>
        </Columns>
      </Specimens>

      <Specimens
        label="Frame"
        note="Clipped cover image. wide is 16 / 9 — landing heroes. Pages do not set aspect-ratio."
      >
        <Frame src={publicUrl('home/friends.jpg')} ratio="wide" />
      </Specimens>

      <Specimens
        label="Columns fill"
        note="As many 320px tracks as the container holds — 1, 2 or 3 in this column. Resize the window. The participants page is this, not a 12-column grid."
      >
        <Columns columns="fill" gap={5}>
          <Card tone="support" title="Maria">
            Card opened
          </Card>
          <Card tone="accent" title="Alex">
            Waiting
          </Card>
          <Card tone="info" title="Sasha">
            Invite sent
          </Card>
          <Card tone="success" title="Katya">
            Knows their giftee
          </Card>
          <Card tone="warning" title="Dima">
            No giftee yet
          </Card>
          <Card tone="neutral" title="Olya">
            It is you
          </Card>
        </Columns>
      </Specimens>

      <Specimens
        label="Well"
        note="Page-coloured inset so a nested Panel keeps its hairline. stage is the demo frame."
      >
        <Well>
          <Container width="narrow">
            <Panel padding="medium">
              <Typography role="body/sm">A panel sitting in a well.</Typography>
            </Panel>
          </Container>
        </Well>
      </Specimens>

      <Specimens
        label="Footer"
        note="Inverse site chrome. Wordmark, three link columns, legal row. Always near-black."
      >
        <Footer />
      </Specimens>

      <Specimens
        label="Page + AppHeader + Brand"
        note="Page is the viewport. Shell keeps header and main at least one screen tall, so the inverse footer stays below the fold. AppHeader is the sticky bar."
      >
        <Well>
          <Page flush>
            <AppHeader
              sticky={false}
              actions={
                <>
                  <Link href="#layout">Back to the app</Link>
                  <ThemeToggle />
                </>
              }
            >
              <Cluster gap={6}>
                <Brand href="#layout" />
                <Typography role="title/eyebrow">UI kit</Typography>
              </Cluster>
            </AppHeader>
            <Container width="measure" gutter="narrow" pad="page">
              <Typography role="body/sm">Page body sits under the header.</Typography>
            </Container>
          </Page>
        </Well>
      </Specimens>

      <Specimens
        label="Hamburger + Drawer"
        note="The hamburger opens a panel that slides in from the left. Use it on a narrow header — not a dropdown."
      >
        <DrawerSpecimen />
      </Specimens>

      <Specimens label="Link" note="plain — a quiet text link. chip — a pill in a nav.">
        <Cluster gap={4}>
          <Link href="#layout">Plain link</Link>
          <Link href="#type" variant="chip">
            Typography
          </Link>
          <Link href="#buttons" variant="chip">
            Buttons
          </Link>
        </Cluster>
      </Specimens>

      <Specimens label="InlineCode + Snippet" note="Mono, padded. Inline for a name; Snippet for a block to copy.">
        <Stack gap={3} wide>
          <Typography role="body/sm">
            Wrap a token in <InlineCode>--color-accent</InlineCode> rather than a hex.
          </Typography>
          <Snippet>{`<Typography role="title/page">Sign in</Typography>`}</Snippet>
        </Stack>
      </Specimens>

      <Specimens label="Badge + IconBox" note="A count chip, and a sized slot for a 1em icon.">
        <Cluster gap={4}>
          <Badge>12</Badge>
          <IconBox size="xl">
            <GiftIcon />
          </IconBox>
          <IconBox size="md">
            <GiftIcon />
          </IconBox>
        </Cluster>
      </Specimens>

      <Specimens
        label="RowList"
        note="Hairline rows. catalog is the type-specimen row (padding + a bottom rule); large is for a list of people."
      >
        <RowList>
          <Row>
            <Typography role="body/sm">First row</Typography>
          </Row>
          <Row>
            <Typography role="body/sm">Second row</Typography>
          </Row>
          <Row size="large" align="center">
            <Typography role="body/sm">Large row</Typography>
          </Row>
        </RowList>
      </Specimens>

      <Specimens
        label="Specimens + FoldPanel"
        note="These two are the kit itself: a labelled demo block, and a folding Panel. You are looking at both."
      >
        <Typography role="body/sm">
          This row is a Specimens. The section around it is a FoldPanel.
        </Typography>
      </Specimens>
    </FoldPanel>
  );
}
